import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import {
  approvalRoleLabel,
  DocumentApprovalRole,
  onBehalfOptions,
  requiresCmInventory,
  requiresCustomer,
} from 'src/app/enums/document-approval-role';
import { FileMetadata, FileSrc } from 'src/app/models/helpers.model';
import {
  OrderDocumentForStages,
  OrderDocumentPreview,
} from 'src/app/models/order-document';
import { AttachmentsService } from 'src/app/services/attachments.service';
import {
  DocumentPrintPreferenceService,
  PRINT_LAYOUT_STAGE_DEFAULTS,
  PrintLayoutStage,
} from 'src/app/services/document-print-preference.service';
import { OrdersService } from 'src/app/services/orders.service';
import { environment } from 'src/environments/environment';
import { REJECTION_REASONS } from 'src/app/shared/constants/rejection-reasons';

type SlotState = 'approved' | 'rejected' | 'pending' | 'na';

// ECDEV-176: Stage-3 document approval popup. Reuses the gallery navigation pattern
// (prev/next + counter + keyboard) and the pdf-img-preview viewer, and adds a per-role
// approval footer (approve / reject / status chips). The acting role comes from the caller,
// which derives it from the signed-in user's role.
@Component({
  selector: 'app-document-approval-preview',
  templateUrl: './document-approval-preview.component.html',
  styleUrls: ['./document-approval-preview.component.scss'],
})
export class DocumentApprovalPreviewComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() items: OrderDocumentForStages[] = [];
  @Input() startIndex = 0;
  @Input() actingRole: DocumentApprovalRole = DocumentApprovalRole.CmInventory;
  // ECDEV-412: a CM Admin picks whose approval they are recording (Customer, CM Inventory or
  // both) instead of always acting as actingRole.
  @Input() canActOnBehalf = false;
  // ECDEV-415: that pick is the documents list's one choice, shared with the list (None until
  // made). Changing it here changes it there too.
  @Input() selectedParty: DocumentApprovalRole = DocumentApprovalRole.None;
  @Output() selectedPartyChange = new EventEmitter<DocumentApprovalRole>();
  @Input() canApprove = true;
  // ECDEV-255: when approving is gated by something the viewer can act on (Stages 2A/2B
  // not approved yet), the caller passes the explanation to show in the footer.
  @Input() blockedReason = '';

  @Output() approve = new EventEmitter<{
    doc: OrderDocumentForStages;
    role: DocumentApprovalRole;
  }>();
  @Output() reject = new EventEmitter<{
    doc: OrderDocumentForStages;
    role: DocumentApprovalRole;
    reason: string;
  }>();
  @Output() modalState = new EventEmitter<{ isClose: boolean }>();

  index = 0;
  // ECDEV-174: both server-rendered variants of the current document. The ids are kept alongside
  // the srcs so download/print pick the right file without re-parsing the viewer URL.
  pdfSrc: FileSrc | null = null;
  pdfImgSrc: FileSrc | null = null;
  attachmentId: string | null = null;
  attachmentWithTransparentBgId: string | null = null;
  /** ECDEV-365: which stage's first-visit default this modal opens with. */
  private readonly printStage = PrintLayoutStage.Stage3;
  /**
   * true = include the pre-printed form background. ECDEV-365: this modal is the Stage-3 one, where
   * the first visit shows the FULL document — reviewers read working copies on plain paper, so
   * nothing is stripped. A choice the user has made for this template still wins; see
   * DocumentPrintPreferenceService.
   */
  showlayout = PRINT_LAYOUT_STAGE_DEFAULTS[this.printStage];
  fileName = '';
  loadingDoc = false;
  // The rejection-reason dropdown is only revealed once the user clicks Reject.
  isRejecting = false;
  rejectReason: string | null = null;
  rejectionReasons = REJECTION_REASONS;
  readonly role = DocumentApprovalRole;
  private destroy = new Subject<boolean>();

  constructor(
    private attachmentsService: AttachmentsService,
    private ordersService: OrdersService,
    private printPrefs: DocumentPrintPreferenceService
  ) {}

  ngOnInit(): void {
    this.setIndex(this.startIndex);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startIndex'] || changes['items']) {
      this.setIndex(this.startIndex);
    }
  }

  get current(): OrderDocumentForStages | undefined {
    return this.items[this.index];
  }

  private setIndex(i: number): void {
    if (!this.items.length) {
      return;
    }
    this.index = Math.min(Math.max(i, 0), this.items.length - 1);
    this.isRejecting = false;
    this.rejectReason = null;
    const doc = this.items[this.index];
    this.fileName = doc.document.name;
    this.loadDocPreview(doc);
  }

  // The previewable PDF is generated server-side from the template + order data, in both the
  // with-background and values-only variants.
  private loadDocPreview(doc: OrderDocumentForStages): void {
    this.pdfSrc = null;
    this.pdfImgSrc = null;
    this.attachmentId = null;
    this.attachmentWithTransparentBgId = null;
    // ECDEV-174: seed the switch from whatever this session already knows, so paging back to a
    // document the user has already flipped shows the right position immediately. The response
    // then confirms it, or supplies it the first time. With nothing stored this is Stage 3's
    // default (ECDEV-365), not a global one.
    this.showlayout = this.printPrefs.get(doc.documentId, this.printStage);
    this.loadingDoc = true;
    this.ordersService
      .getOrderDocumentPreview(doc.orderId, doc.id)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (preview: OrderDocumentPreview) => {
          if (this.current?.id !== doc.id) {
            return; // user navigated away before this resolved
          }
          this.printPrefs.prime(doc.documentId, preview.printWithBackground);
          this.showlayout = this.printPrefs.get(doc.documentId, this.printStage);
          this.attachmentId = preview.attachmentId;
          this.attachmentWithTransparentBgId =
            preview.attachmentWithTransparentBgId;
          const token = JSON.parse(<string>localStorage.getItem('token'));
          const httpHeaders = { Authorization: `Bearer ${token.token}` };
          this.pdfSrc = {
            url: `${environment.apiUrl}/attachments/${preview.attachmentId}`,
            httpHeaders,
            withCredentials: true,
          };
          this.pdfImgSrc = {
            url: `${environment.apiUrl}/attachments/${preview.attachmentWithTransparentBgId}`,
            httpHeaders,
            withCredentials: true,
          };
          this.loadingDoc = false;
        },
        error: () => {
          this.loadingDoc = false;
        },
      });
  }

  /** The variant currently on screen. Null until the preview resolves — the viewer is *ngIf'd. */
  get currentSrc(): FileSrc | null {
    return this.showlayout ? this.pdfSrc : this.pdfImgSrc;
  }

  // ECDEV-174: remember the choice for this document template, for next time in any stage or order.
  onPrintLayoutChange(showlayout: boolean): void {
    this.showlayout = showlayout;
    this.printPrefs.set(this.current?.documentId, showlayout);
  }

  prev(event?: Event): void {
    event?.stopPropagation();
    if (this.index > 0) {
      this.setIndex(this.index - 1);
    }
  }

  next(event?: Event): void {
    event?.stopPropagation();
    if (this.index < this.items.length - 1) {
      this.setIndex(this.index + 1);
    }
  }

  // ---- per-role slot state for the current doc ----
  slotState(doc: OrderDocumentForStages | undefined, role: DocumentApprovalRole): SlotState {
    if (!doc) {
      return 'na';
    }
    const required =
      role === this.role.Customer
        ? requiresCustomer(doc.document.approvalRole)
        : requiresCmInventory(doc.document.approvalRole);
    if (!required) {
      return 'na';
    }
    if (role === this.role.Customer) {
      if (doc.customerApprovedOn) return 'approved';
      if (doc.customerRejectedOn) return 'rejected';
      return 'pending';
    }
    if (doc.cmInventoryApprovedOn) return 'approved';
    if (doc.cmInventoryRejectedOn) return 'rejected';
    return 'pending';
  }

  noApprovalRequired(doc: OrderDocumentForStages | undefined): boolean {
    return !doc || (doc.document.approvalRole ?? 0) === this.role.None;
  }

  // The required slots nobody has approved or rejected yet.
  pendingRoles(doc: OrderDocumentForStages | undefined): DocumentApprovalRole {
    let pending = DocumentApprovalRole.None;
    if (this.slotState(doc, this.role.Customer) === 'pending') {
      pending |= this.role.Customer;
    }
    if (this.slotState(doc, this.role.CmInventory) === 'pending') {
      pending |= this.role.CmInventory;
    }
    return pending;
  }

  readonly onBehalfOptions = onBehalfOptions(
    DocumentApprovalRole.Customer | DocumentApprovalRole.CmInventory
  );

  get onBehalfLabel(): string {
    return approvalRoleLabel(this.selectedParty);
  }

  selectParty(role: DocumentApprovalRole): void {
    this.selectedParty = role;
    this.selectedPartyChange.emit(role);
  }

  // The slot(s) Approve / Reject act on: a CM Admin's pick narrowed to the parties still pending
  // on this document. None (nothing picked, or nothing pending for the pick) disables them.
  get roleToActOn(): DocumentApprovalRole {
    return this.canActOnBehalf
      ? this.selectedParty & this.pendingRoles(this.current)
      : this.actingRole;
  }

  // Whether the current user can approve/reject the current document.
  canActOnCurrent(): boolean {
    const doc = this.current;
    if (!doc || !this.canApprove) {
      return false;
    }
    if (this.canActOnBehalf) {
      return this.pendingRoles(doc) !== this.role.None;
    }
    return this.slotState(doc, this.actingRole) === 'pending';
  }

  onApprove(): void {
    const doc = this.current;
    if (!doc || !this.canActOnCurrent() || !this.roleToActOn) {
      return;
    }
    this.approve.emit({ doc, role: this.roleToActOn });
  }

  // First Reject click just reveals the reason dropdown; the confirm click submits.
  startReject(): void {
    if (!this.canActOnCurrent() || !this.roleToActOn) {
      return;
    }
    this.isRejecting = true;
  }

  cancelReject(): void {
    this.isRejecting = false;
    this.rejectReason = null;
  }

  onReject(): void {
    const doc = this.current;
    if (!doc || !this.canActOnCurrent() || !this.roleToActOn || !this.rejectReason) {
      return;
    }
    this.reject.emit({ doc, role: this.roleToActOn, reason: this.rejectReason });
    this.isRejecting = false;
  }

  close(): void {
    this.modalState.emit({ isClose: true });
  }

  backdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    }
  }

  // ECDEV-174: pick the id matching what is on screen rather than splitting it back out of the
  // viewer URL — download and print must produce the variant the user is looking at. Stage 3 used
  // to hard-code the with-background render for both.
  private currentAttachmentId(): string | null {
    return this.showlayout ? this.attachmentId : this.attachmentWithTransparentBgId;
  }

  downloadDoc(): void {
    const attachmentId = this.currentAttachmentId();
    if (!attachmentId) {
      return;
    }
    forkJoin([
      this.attachmentsService.getAttachment(attachmentId),
      this.attachmentsService.getMetadata(attachmentId),
    ])
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp: [ArrayBuffer, FileMetadata]) => {
          const blob = new Blob([resp[0]], { type: 'application/pdf' });
          const anchorEl = document.createElement('a');
          anchorEl.href = URL.createObjectURL(blob);
          anchorEl.setAttribute('download', `${resp[1].fileName}`);
          anchorEl.click();
        },
      });
  }

  printDoc(): void {
    const attachmentId = this.currentAttachmentId();
    if (!attachmentId) {
      return;
    }
    this.attachmentsService
      .getAttachment(attachmentId)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp: ArrayBuffer) => {
          const blob = new Blob([resp], { type: 'application/pdf' });
          const frameEl = document.createElement('iframe');
          frameEl.style.display = 'none';
          frameEl.src = URL.createObjectURL(blob);
          document.body.appendChild(frameEl);
          frameEl.onload = () => {
            frameEl.contentWindow?.focus();
            frameEl.contentWindow?.print();
          };
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
